import {
  Box,
  Button,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  Link,
  VStack,
} from '@chakra-ui/react';
import { FiExternalLink, FiDownload, FiBarChart2 } from 'react-icons/fi';
import type { Profile } from '@/lib/sdk/profiling';

interface FlameGraphProps {
  profile: Profile;
}

export function FlameGraph({ profile }: FlameGraphProps) {
  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );
  const borderColor = useColorModeValue('border-default', 'border-default');

  // Use the download URL from the profile (includes signed token for cross-origin access)
  const downloadUrl = profile.download_url || '';
  // Build absolute URL for speedscope
  const absoluteDownloadUrl = downloadUrl.startsWith('http')
    ? downloadUrl
    : `${window.location.origin}${downloadUrl}`;
  const speedscopeUrl = `https://www.speedscope.app/#profileURL=${encodeURIComponent(absoluteDownloadUrl)}`;

  if (!profile.download_url) {
    return (
      <Box
        p={8}
        textAlign="center"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        bgGradient={cardGradient}
      >
        <Text color="text-muted" mb={4}>
          Profile download URL not available.
        </Text>
        <Text color="text-secondary" fontSize="sm">
          The profile may still be processing.
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Flame Graph Viewer Card */}
      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        bgGradient={cardGradient}
        p={8}
      >
        <VStack spacing={6}>
          <Icon as={FiBarChart2} boxSize={16} color="brand.500" />
          <VStack spacing={2}>
            <Text color="text-primary" fontSize="lg" fontWeight="medium">
              Interactive Flame Graph
            </Text>
            <Text color="text-secondary" fontSize="sm" textAlign="center" maxW="400px">
              Click the button below to open this profile in Speedscope, an interactive flame graph viewer.
            </Text>
          </VStack>
          <HStack spacing={3}>
            <Button
              as={Link}
              href={downloadUrl}
              isExternal
              size="md"
              variant="outline"
              leftIcon={<Icon as={FiDownload} />}
              borderColor="border-default"
              _hover={{ borderColor: 'border-hover', textDecoration: 'none' }}
            >
              Download .pb File
            </Button>
            <Button
              as={Link}
              href={speedscopeUrl}
              isExternal
              size="md"
              colorScheme="brand"
              leftIcon={<Icon as={FiExternalLink} />}
              _hover={{ textDecoration: 'none' }}
            >
              Open in Speedscope
            </Button>
          </HStack>
        </VStack>
      </Box>

      <Text color="text-muted" fontSize="xs" textAlign="center">
        Powered by{' '}
        <Link href="https://www.speedscope.app" isExternal color="brand.500">
          speedscope.app
        </Link>
        {' '}- Opens in a new tab for best performance
      </Text>
    </VStack>
  );
}
